"use client";

import { Z_INDEX_BACKDROP } from "@/app/layout/LayoutConstants";
import { Backdrop } from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type BackdropOpenChangedHandler = (open: boolean) => void;

interface BodyBackdropContext {
  setBackdropOpen: (open: boolean) => void;
  backdropOpen: boolean;
  registerBackdropOpenChanged: (cb: BackdropOpenChangedHandler) => void;
}

let isBackdropOpenGlobal = false;

const bodyBackdropContext = createContext<BodyBackdropContext | undefined>({
  setBackdropOpen: () => {
    throw new Error("setBackdropOpen not implemented");
  },
  backdropOpen: false,
  registerBackdropOpenChanged: () => {
    throw new Error("onBackdropOpenChanged not implemented");
  },
});

export function BodyBackdropProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const backdropOpenChangedHandlers = useRef<((open: boolean) => void)[]>([]);
  const [backdropOpen, setBackdropOpen] = useState(isBackdropOpenGlobal);
  const handleSetBackdropOpen = useCallback((open: boolean) => {
    isBackdropOpenGlobal = open;
    setBackdropOpen(open);
    backdropOpenChangedHandlers.current.forEach((cb) => cb(open));
  }, []);
  const contextValue: BodyBackdropContext = {
    setBackdropOpen: handleSetBackdropOpen,
    backdropOpen,
    registerBackdropOpenChanged: (cb: BackdropOpenChangedHandler) => {
      backdropOpenChangedHandlers.current.push(cb);
    },
  };

  return (
    <bodyBackdropContext.Provider value={contextValue}>
      {children}
      <Backdrop
        open={backdropOpen}
        onClick={() => handleSetBackdropOpen(false)}
        sx={{
          zIndex: Z_INDEX_BACKDROP,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
        }}
      />
    </bodyBackdropContext.Provider>
  );
}

export function useBodyBackdrop({
  onBackdropOpenChanged,
}: {
  onBackdropOpenChanged?: BackdropOpenChangedHandler;
} = {}) {
  const context = useContext(bodyBackdropContext);
  if (!context) {
    throw new Error(
      "useBodyBackdrop must be used within a BodyBackdropProvider",
    );
  }
  useEffect(() => {
    if (onBackdropOpenChanged) {
      context.registerBackdropOpenChanged(onBackdropOpenChanged);
    }
  }, [context, onBackdropOpenChanged]);

  return context;
}
